import { IPaginatedList, IReadOptions, AjaxHelper, Source } from '@samc/common';
import { IListType } from '../Interfaces';

export function processQuery (options: IReadOptions) {
  const query: Record<string, any> = {
    'page[number]': options.page
  };

  if (options.pageSize !== Number.MAX_SAFE_INTEGER)
    query['page[size]'] = options.pageSize;

  if (options.filter !== undefined) {
    const keys = Object.keys(options.filter);
    for (let i = 0; i < keys.length; ++i) {
      query[keys[i]] = options.filter[keys[i]].filter;
    }
  }
  return query;
}

export class PageListDataSource<T> extends Source<T> {
  private _helper: AjaxHelper;

  public constructor(url: string, private _query?: Record<string, any>, pageSize = Number.MAX_SAFE_INTEGER) {
    super(pageSize);
    this._helper = new AjaxHelper(url, '', '', null);
  }

  protected async getRows(options: IReadOptions, abortController: AbortController): Promise<IPaginatedList<T>> {
    const cleanedQuery: Record<string, any> = {};
    if (this._query) {
      for (const [key, value] of Object.entries(this._query)) {
        if (value !== null && value !== undefined) {
          cleanedQuery[key] = value;
        }
      }
    }
    return await this._helper.getData<IPaginatedList<T>>('', { ...processQuery(options), ...cleanedQuery }, { signal: abortController.signal });
  }
}

export abstract class CachedDataSource<T> extends Source<T> {
  private _items: T[] | null = null;

  public get isLoaded(): boolean { return this._items !== null; }
  public get cachedItems(): T[] { return this._items ?? []; }

  public constructor(pageSize = Number.MAX_SAFE_INTEGER) {
    super(pageSize);
  }

  protected abstract loadData(options: IReadOptions, abortController: AbortController): Promise<T[]> | T[];

  /**
   * Helper method to get the static data that should always be included at the top of the items list
   * 
   * @returns {T[]} Returns the static data that should always be included at the top of the items list
   */
  protected getStaticData(): T[] {
    return [];
  }

  protected async getRows(options: IReadOptions, abortController: AbortController): Promise<IPaginatedList<T>> {
    if (this._items === null) {
      try {
        let data = this.loadData(options, abortController);
        if (!Array.isArray(data)) {
          data = await data;
        }

        if (!Array.isArray(data)) {
          this._items = null;
          $log('Returned object is not an array');
          return { items: [], fullCount: 0 };
        }
        this._items = data;
      } catch (err: unknown) {
        $log('Failed to load array data source', err);
        return { items: [], fullCount: 0 };
      }
    }

    const keys = options.filter !== undefined ? Object.keys(options.filter) : [];
    const fiteredItems: T[] = this._items.filter((item: any) => {
      if (keys.length > 0) {
        for (let i = 0; i < keys.length; ++i) {
          if (options.filter !== undefined) {
            if (item[keys[i]].toString().toLowerCase().startsWith(options.filter[keys[i]].filter.toString().toLowerCase())) {
              return true;
            }
          }
        }
      }
      else {
        // If Nothing is there in filter
        return true;
      }
    });

    const resultItems = [...this.getStaticData(), ...fiteredItems];

    return {
      items: resultItems,
      fullCount: resultItems.length
    };
  }

  public find(predicate: (x: T) => boolean): T | undefined {
    return this._items?.find(predicate);
  }

  public async addItem(item: T): Promise<void> {
    if (this._items !== null) {
      this._items.push(item);

      await this.read();
    }
  }
}

export class PageListCachedDataSource<T> extends CachedDataSource<T> {
  private _helper: AjaxHelper;

  public constructor(url: string, private _query?: Record<string, any>, pageSize = Number.MAX_SAFE_INTEGER) {
    super(pageSize);
    this._helper = new AjaxHelper(url, '', '', null);
  }

  protected async loadData(options: IReadOptions, abortController: AbortController): Promise<T[]> {
    const result = await this._helper.getData<IPaginatedList<T>>('', { ...processQuery(options), ...this._query }, { signal: abortController.signal });
    return result.items;
  }
}

export class ArrayDataSource<T> extends CachedDataSource<T> {
  private _helper: AjaxHelper;

  public constructor(url: string, private _query?: Record<string, any>) {
    super(Number.MAX_SAFE_INTEGER);
    this._helper = new AjaxHelper(url, '', '', null);
  }

  protected async loadData(options: IReadOptions, abortController: AbortController): Promise<T[]> {
    return await this._helper.getData<T[]>('', { ...processQuery(options), ...this._query }, { signal: abortController.signal });
  }
}

export function FilterCachedList<T extends IListType<T>>(detail: T, search: string) {
  return detail.name.startsWith(search);
}